import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { ROUTES } from '../../constants/routes';

/**
 * Booking confirmation component shown after successful booking
 */
const BookingConfirmation = ({
  booking,
  businessName,
  meetingType,
  onBookAnother,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const startTime = booking?.start_time ? new Date(booking.start_time) : null;
  const endTime = booking?.end_time ? new Date(booking.end_time) : null;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
      <CheckCircleIcon 
        sx={{ fontSize: { xs: 48, sm: 64 }, color: 'success.main', mb: 2 }} 
      />
      
      <Typography 
        variant="h5" 
        gutterBottom 
        color="success.main"
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
      >
        {t('booking.success')}
      </Typography>
      
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        {t('booking.successMessage')}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: 'left', mb: 3 }}>
        <Typography 
          variant="subtitle1" 
          gutterBottom 
          fontWeight="bold"
          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
        >
          {t('booking.bookingDetails')}
        </Typography>
        
        {businessName && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ minWidth: 80, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              Business:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              {businessName}
            </Typography>
          </Box>
        )}
        
        {meetingType && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ minWidth: 80, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              {t('booking.service')}:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              {meetingType.description || meetingType.name}
            </Typography>
          </Box>
        )}
        
        {startTime && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ minWidth: 80, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              {t('booking.date')}:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              {formatDate(startTime, 'EEEE, MMMM d, yyyy')}
            </Typography>
          </Box>
        )}
        
        {startTime && endTime && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ minWidth: 80, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              {t('booking.time')}:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              {formatTime(startTime, 'HH:mm')} - {formatTime(endTime, 'HH:mm')}
            </Typography>
          </Box>
        )}

        {booking?.confirmation_number && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ minWidth: 80, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              Confirmation:
            </Typography>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              {booking.confirmation_number}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mb: 3, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
      >
        {t('booking.confirmationSent')}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button
          variant="outlined"
          startIcon={<EventIcon />}
          onClick={() => navigate(ROUTES.MY_BOOKINGS)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {t('booking.viewMyBookings')}
        </Button>
        <Button
          variant="contained"
          onClick={onBookAnother}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {t('booking.bookAnother')}
        </Button>
      </Box>
    </Paper>
  );
};

export default BookingConfirmation;
